import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DocumentsFilters,
  type DepartmentOption,
} from "@/features/documents/components/DocumentsFilters";
import { DocumentLibrariesFilters } from "@/features/documents/components/DocumentLibrariesFilters";
import { DocumentsTable } from "@/features/documents/components/DocumentsTable";
import { DocumentLibrariesTable } from "@/features/documents/components/DocumentLibrariesTable";
import { CreateLibraryDialog } from "@/features/documents/components/CreateLibraryDialog";
import { UploadDocumentDialog } from "@/features/documents/components/UploadDocumentDialog";
import {
  useDocuments,
  useScopedDocumentDepartments,
} from "@/features/documents/hooks/useDocumentsQueries";
import { useDocumentLibraries } from "@/features/documents/hooks/useDocumentLibrariesQueries";
import { useDocumentsPermissions } from "@/features/documents/hooks/useDocumentsPermissions";
import { documentService } from "@/features/documents/services/documentService";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { useDebounce } from "@/hooks/useDebounce";
import { handleApiError } from "@/lib/error-handler";
import { useUser } from "@/features/auth/store";
import { isEditor } from "@/lib/auth-utils";

const PAGE_SIZE = 25;
type TabKey = "documents" | "libraries";

function parseTab(raw: string | null, canManage: boolean): TabKey {
  if (raw === "libraries" && canManage) return "libraries";
  return "documents";
}

export default function DocumentsPage() {
  const user = useUser();
  const { canUpload, canManage } = useDocumentsPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"), canManage);

  const setTab = (next: TabKey) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "documents") params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  };

  const [searchInput, setSearchInput] = useState("");
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [libraryId, setLibraryId] = useState<string | undefined>();
  const [language, setLanguage] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const search = useDebounce(searchInput, 300);

  const { data: departmentsData } = useDepartments();
  const allDepartments = useMemo(
    () => departmentsData?.items ?? [],
    [departmentsData],
  );
  const scopedDepartments = useScopedDocumentDepartments(allDepartments);

  // Default scoped users to their home department when unset.
  useEffect(() => {
    if (departmentId || !user?.departmentId || !isEditor(user)) return;
    if (scopedDepartments.some((d) => d.id === user.departmentId)) {
      setDepartmentId(user.departmentId);
    }
  }, [departmentId, user, scopedDepartments]);

  const { data: librariesForDropdown } = useDocumentLibraries(
    { limit: 200 },
    { enabled: tab === "documents" },
  );
  const libraries = useMemo(
    () => librariesForDropdown?.items ?? [],
    [librariesForDropdown],
  );

  const libraryListFilters = useMemo(
    () => ({ search, departmentId, limit: 200 }),
    [search, departmentId],
  );
  const {
    data: librariesListData,
    isLoading: librariesLoading,
    error: librariesError,
  } = useDocumentLibraries(libraryListFilters, {
    enabled: tab === "libraries" && canManage,
  });
  const filteredLibraries = useMemo(
    () => librariesListData?.items ?? [],
    [librariesListData],
  );
  const librariesTotal = librariesListData?.meta.total ?? 0;

  const departmentOptions: DepartmentOption[] = useMemo(
    () => scopedDepartments.map((d) => ({ id: d.id, label: d.nameAr })),
    [scopedDepartments],
  );
  const libraryOptions: DepartmentOption[] = useMemo(
    () => libraries.map((l) => ({ id: l.id, label: l.title })),
    [libraries],
  );
  const departmentLookup: Record<string, string> = useMemo(
    () => Object.fromEntries(scopedDepartments.map((d) => [d.id, d.nameAr])),
    [scopedDepartments],
  );
  const libraryLookup: Record<string, string> = useMemo(
    () => Object.fromEntries(libraries.map((l) => [l.id, l.title])),
    [libraries],
  );

  const lockedDepartmentId =
    isEditor(user) && user?.departmentId ? user.departmentId : undefined;

  // Users without manage access cannot stay on the libraries tab.
  useEffect(() => {
    if (!canManage && searchParams.get("tab") === "libraries") {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("tab");
          return params;
        },
        { replace: true },
      );
    }
  }, [canManage, searchParams, setSearchParams]);

  const filters = useMemo(
    () => ({ search, departmentId, libraryId, language, page, limit: PAGE_SIZE }),
    [search, departmentId, libraryId, language, page],
  );

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [search, departmentId, libraryId, language]);

  const { data, isLoading, error } = useDocuments(filters);
  const documents = useMemo(() => data?.items ?? [], [data]);
  const pageCount = Math.max(1, data?.pagination.pageCount ?? 1);
  const total = data?.pagination.total ?? 0;

  const hasActiveFilters = Boolean(
    searchInput || departmentId || libraryId || language,
  );
  const hasLibraryActiveFilters = Boolean(searchInput || departmentId);

  async function onExport() {
    setExporting(true);
    try {
      await documentService.exportCsv({
        search,
        departmentId,
        libraryId,
        language,
        ...(selected.size > 0 && { ids: [...selected].join(",") }),
      });
    } catch (e) {
      handleApiError(e);
    } finally {
      setExporting(false);
    }
  }

  const idOf = (doc: (typeof documents)[number]) => doc.id;
  const allOnPageSelected =
    documents.length > 0 && documents.every((d) => selected.has(idOf(d)));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) documents.forEach((d) => next.delete(idOf(d)));
      else documents.forEach((d) => next.add(idOf(d)));
      return next;
    });

  return (
    <AdminPageShell
      title="إدارة المستندات"
      description="مستندات الدوائر والملفات المرفوعة"
      isLoading={tab === "documents" ? isLoading : librariesLoading}
      error={tab === "documents" ? error : librariesError}
      actions={
        tab === "documents" ? (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onExport}
            disabled={exporting || total === 0}
          >
            <Download className="w-4 h-4 ml-2" />
            {selected.size > 0 ? `تصدير (${selected.size})` : "تصدير CSV"}
          </Button>
          {canUpload ? (
            <UploadDocumentDialog
              departments={departmentOptions}
              lockedDepartmentId={lockedDepartmentId}
              canCreateLibrary={canManage}
            />
          ) : null}
        </div>
        ) : canManage ? (
          <CreateLibraryDialog
            departments={departmentOptions}
            lockedDepartmentId={lockedDepartmentId}
          />
        ) : undefined
      }
    >
      {canManage && (
        <TabsList className="mb-4">
          <TabsTrigger
            data-state={tab === "documents" ? "active" : "inactive"}
            onClick={() => setTab("documents")}
          >
            المستندات
          </TabsTrigger>
          <TabsTrigger
            data-state={tab === "libraries" ? "active" : "inactive"}
            onClick={() => setTab("libraries")}
          >
            المكتبات
          </TabsTrigger>
        </TabsList>
      )}

      {tab === "libraries" && canManage ? (
        <div className="space-y-4">
          <DocumentLibrariesFilters
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            departmentId={departmentId}
            onDepartmentChange={setDepartmentId}
            departments={departmentOptions}
            onClearFilters={() => {
              setSearchInput("");
              setDepartmentId(lockedDepartmentId);
            }}
            hasActiveFilters={hasLibraryActiveFilters}
            departmentLocked={!!lockedDepartmentId}
          />
          <DocumentLibrariesTable
            libraries={filteredLibraries}
            departmentLookup={departmentLookup}
            departments={departmentOptions}
          />
          <p className="text-sm text-muted-foreground">
            الإجمالي: {librariesTotal}
          </p>
        </div>
      ) : (
      <div className="space-y-4">
        <DocumentsFilters
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          departmentId={departmentId}
          onDepartmentChange={setDepartmentId}
          departments={departmentOptions}
          libraryId={libraryId}
          onLibraryChange={setLibraryId}
          libraries={libraryOptions}
          onClearFilters={() => {
            setSearchInput("");
            setDepartmentId(lockedDepartmentId);
            setLibraryId(undefined);
            setLanguage(undefined);
            setSelected(new Set());
          }}
          hasActiveFilters={hasActiveFilters}
          departmentLocked={!!lockedDepartmentId}
          language={language}
          onLanguageChange={setLanguage}
        />
        <DocumentsTable
          documents={documents}
          departmentLookup={departmentLookup}
          libraryLookup={libraryLookup}
          canManage={canManage}
          page={page}
          pageCount={pageCount}
          onPageChange={setPage}
          selected={selected}
          allOnPageSelected={allOnPageSelected}
          onToggle={toggle}
          onToggleAll={toggleAll}
        />
        <p className="text-sm text-muted-foreground">الإجمالي: {total}</p>
      </div>
      )}
    </AdminPageShell>
  );
}
